import React, { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { pageMetadata } from '../config/seo';

const API_KEYS: string[] = [
  import.meta.env.VITE_YOUTUBE_API_KEY,
  import.meta.env.VITE_YOUTUBE_API_KEY_2,
].filter(Boolean);
const CHANNEL_ID = import.meta.env.VITE_YOUTUBE_CHANNEL_ID;
const CACHE_KEY = 'agility_yt_page_v1';
const CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours

interface VideoItem {
  videoId: string;
  title: string;
  description: string;
  thumbnail: string;
  publishedAt: string;
  durationSecs: number;
}

function decodeHtml(str: string): string {
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

function parseDuration(iso: string): number {
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!m) return 0;
  return (parseInt(m[1] ?? '0') * 3600) +
         (parseInt(m[2] ?? '0') * 60) +
          parseInt(m[3] ?? '0');
}

function formatDuration(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getRelativeTime(dateString: string): string {
  const published = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - published.getTime()) / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  if (diffYears >= 1) return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
  if (diffMonths >= 1) return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
  if (diffWeeks >= 1) return diffWeeks === 1 ? '1 week ago' : `${diffWeeks} weeks ago`;
  if (diffDays >= 1) return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  return 'Today';
}

async function fetchVideosPage(pageToken?: string, keyIndex = 0): Promise<{ videos: VideoItem[]; nextToken: string | null }> {
  if (keyIndex >= API_KEYS.length) throw new Error('All keys exhausted');
  const key = API_KEYS[keyIndex];

  const params = new URLSearchParams({
    part: 'snippet',
    channelId: CHANNEL_ID,
    maxResults: '20',
    order: 'date',
    type: 'video',
    key,
  });
  if (pageToken) params.set('pageToken', pageToken);

  const searchRes = await fetch(`https://www.googleapis.com/youtube/v3/search?${params}`);
  if (searchRes.status === 403) return fetchVideosPage(pageToken, keyIndex + 1);
  if (!searchRes.ok) throw new Error('Search failed');
  const searchData = await searchRes.json();

  const items: any[] = searchData.items ?? [];
  if (!items.length) return { videos: [], nextToken: null };

  const ids = items.map((i) => i.id.videoId).join(',');
  const detailsRes = await fetch(
    `https://www.googleapis.com/youtube/v3/videos?part=contentDetails&id=${ids}&key=${key}`
  );
  if (detailsRes.status === 403) return fetchVideosPage(pageToken, keyIndex + 1);
  if (!detailsRes.ok) throw new Error('Details failed');
  const detailsData = await detailsRes.json();

  const durationMap: Record<string, number> = {};
  (detailsData.items ?? []).forEach((d: any) => {
    durationMap[d.id] = parseDuration(d.contentDetails.duration);
  });

  const videos: VideoItem[] = items
    .filter((i) => (durationMap[i.id.videoId] ?? 0) > 180)
    .map((i) => ({
      videoId: i.id.videoId,
      title: decodeHtml(i.snippet.title),
      description: decodeHtml(i.snippet.description),
      thumbnail: i.snippet.thumbnails?.high?.url ?? i.snippet.thumbnails?.medium?.url ?? '',
      publishedAt: i.snippet.publishedAt,
      durationSecs: durationMap[i.id.videoId] ?? 0,
    }));

  return { videos, nextToken: searchData.nextPageToken ?? null };
}

type DurationFilter = 'all' | 'medium' | 'long';
type SortOrder = 'newest' | 'oldest';

const YouTube = () => {
  const [allVideos, setAllVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [durationFilter, setDurationFilter] = useState<DurationFilter>('all');
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    if (!API_KEYS.length || !CHANNEL_ID) { setError(true); setLoading(false); return; }

    // Serve from cache if fresh
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { videos, nextToken, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL && videos?.length) {
          setAllVideos(videos);
          setNextPageToken(nextToken);
          setLoading(false);
          return;
        }
      }
    } catch (_) {}

    fetchVideosPage()
      .then(({ videos, nextToken }) => {
        setAllVideos(videos);
        setNextPageToken(nextToken);
        setLoading(false);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ videos, nextToken, timestamp: Date.now() }));
        } catch (_) {}
      })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  const loadMore = () => {
    if (!nextPageToken || loadingMore) return;
    setLoadingMore(true);
    fetchVideosPage(nextPageToken)
      .then(({ videos, nextToken }) => {
        setAllVideos((prev) => [...prev, ...videos]);
        setNextPageToken(nextToken);
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  };

  const displayed = useMemo(() => {
    let list = [...allVideos];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (v) => v.title.toLowerCase().includes(q) || v.description.toLowerCase().includes(q)
      );
    }
    if (durationFilter === 'medium') list = list.filter((v) => v.durationSecs <= 1200);
    if (durationFilter === 'long') list = list.filter((v) => v.durationSecs > 1200);
    list.sort((a, b) => {
      const diff = new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
      return sortOrder === 'newest' ? diff : -diff;
    });
    return list;
  }, [allVideos, search, durationFilter, sortOrder]);

  const FilterPanel = () => (
    <div className="space-y-8">
      {/* Search */}
      <div>
        <h3 className="text-white font-semibold mb-3 text-xs uppercase tracking-widest">Search</h3>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={15} />
          <input
            type="text"
            placeholder="Search videos…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>
      </div>

      {/* Duration */}
      <div>
        <h3 className="text-white font-semibold mb-3 text-xs uppercase tracking-widest">Duration</h3>
        <div className="space-y-1.5">
          {([
            { value: 'all', label: 'All Videos' },
            { value: 'medium', label: '4 – 20 min' },
            { value: 'long', label: '20+ min' },
          ] as { value: DurationFilter; label: string }[]).map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDurationFilter(opt.value)}
              className="w-full text-left px-4 py-2.5 rounded-lg text-sm transition-all duration-200 font-medium"
              style={
                durationFilter === opt.value
                  ? { backgroundColor: '#2563eb', color: '#fff' }
                  : { color: '#9ca3af' }
              }
              onMouseEnter={(e) => {
                if (durationFilter !== opt.value)
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1f2937';
              }}
              onMouseLeave={(e) => {
                if (durationFilter !== opt.value)
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '';
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sort */}
      <div>
        <h3 className="text-white font-semibold mb-3 text-xs uppercase tracking-widest">Sort By</h3>
        <div className="space-y-1.5">
          {([
            { value: 'newest', label: 'Newest First' },
            { value: 'oldest', label: 'Oldest First' },
          ] as { value: SortOrder; label: string }[]).map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSortOrder(opt.value)}
              className="w-full text-left px-4 py-2.5 rounded-lg text-sm transition-all duration-200 font-medium"
              style={
                sortOrder === opt.value
                  ? { backgroundColor: '#2563eb', color: '#fff' }
                  : { color: '#9ca3af' }
              }
              onMouseEnter={(e) => {
                if (sortOrder !== opt.value)
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1f2937';
              }}
              onMouseLeave={(e) => {
                if (sortOrder !== opt.value)
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '';
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Agility AI YouTube Channel",
    "description": pageMetadata.youtube.description,
    "url": "https://www.agilitytech.ai/youtube",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Agility",
      "url": "https://www.agilitytech.ai"
    },
    "about": {
      "@type": "Organization",
      "name": "Agility",
      "url": "https://www.agilitytech.ai",
      "sameAs": [`https://www.youtube.com/channel/${CHANNEL_ID}`]
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.agilitytech.ai/" },
        { "@type": "ListItem", "position": 2, "name": "Videos", "item": "https://www.agilitytech.ai/youtube" }
      ]
    }
  };

  return (
    <div className="pt-16" style={{ backgroundColor: '#0a0a0a', minHeight: '100vh' }}>
      <SEOHead
        title={pageMetadata.youtube.title}
        description={pageMetadata.youtube.description}
        keywords={pageMetadata.youtube.keywords}
        url="https://www.agilitytech.ai/youtube"
        structuredData={structuredData}
      />

      {/* Hero */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            Our YouTube Channel
          </h1>
          <div
            style={{ backgroundColor: '#2563eb', height: '3px', width: '56px' }}
            className="rounded-full mb-6"
          />
          <p className="text-xl text-gray-400 max-w-2xl">
            Explore our full library of AI insights, tutorials, and thought leadership all in one place.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">

          {error ? (
            <div className="text-center py-20">
              <p className="text-gray-400 text-lg mb-6">Visit our channel to watch the latest videos.</p>
              <a
                href={`https://www.youtube.com/channel/${CHANNEL_ID}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-semibold text-white px-6 py-3 rounded-full transition-opacity hover:opacity-80"
                style={{ backgroundColor: '#2563eb' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><polygon points="5,3 19,12 5,21" /></svg>
                Open YouTube Channel
              </a>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-10">

              {/* Mobile filter toggle */}
              <div className="lg:hidden">
                <button
                  onClick={() => setMobileFiltersOpen((o) => !o)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-700 text-gray-300 text-sm font-medium mb-4"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="11" y1="18" x2="13" y2="18" />
                  </svg>
                  {mobileFiltersOpen ? 'Hide Filters' : 'Show Filters'}
                </button>
                {mobileFiltersOpen && (
                  <div
                    className="rounded-xl p-6 mb-6 border border-gray-800"
                    style={{ backgroundColor: '#111111' }}
                  >
                    <FilterPanel />
                  </div>
                )}
              </div>

              {/* Desktop sidebar */}
              <aside className="hidden lg:block w-56 flex-shrink-0">
                <div className="sticky top-24 rounded-xl p-6 border border-gray-800" style={{ backgroundColor: '#111111' }}>
                  <FilterPanel />
                </div>
              </aside>

              {/* Video grid */}
              <div className="flex-1 min-w-0">
                {loading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="animate-pulse rounded-xl overflow-hidden bg-gray-900">
                        <div className="aspect-video bg-gray-800" />
                        <div className="p-4 space-y-3">
                          <div className="h-4 bg-gray-800 rounded w-3/4" />
                          <div className="h-4 bg-gray-800 rounded w-1/2" />
                          <div className="h-3 bg-gray-800 rounded w-full mt-2" />
                          <div className="h-3 bg-gray-800 rounded w-2/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : displayed.length === 0 ? (
                  <div className="text-center py-24">
                    <p className="text-gray-500 text-lg">No videos match your filters.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-500 text-sm mb-6">
                      Showing {displayed.length} video{displayed.length !== 1 ? 's' : ''}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                      {displayed.map((video) => (
                        <a
                          key={video.videoId}
                          href={`https://www.youtube.com/watch?v=${video.videoId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group rounded-xl overflow-hidden border border-gray-800/50 hover:border-blue-500/40 transition-all duration-300 flex flex-col"
                          style={{ backgroundColor: '#111111' }}
                        >
                          {/* Thumbnail */}
                          <div className="relative aspect-video overflow-hidden">
                            <img
                              src={video.thumbnail}
                              alt={video.title}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <div
                                className="w-14 h-14 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: '#2563eb' }}
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                                  <polygon points="5,3 19,12 5,21" />
                                </svg>
                              </div>
                            </div>
                            {/* Duration badge */}
                            <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-0.5 rounded font-mono">
                              {formatDuration(video.durationSecs)}
                            </div>
                          </div>

                          {/* Info */}
                          <div className="p-4 flex-1 flex flex-col gap-2">
                            <h3 className="text-white font-bold text-sm leading-snug line-clamp-2 group-hover:text-blue-400 transition-colors duration-200">
                              {video.title}
                            </h3>
                            <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 flex-1">
                              {video.description}
                            </p>
                            <p className="text-gray-600 text-xs mt-1">
                              {getRelativeTime(video.publishedAt)}
                            </p>
                          </div>
                        </a>
                      ))}
                    </div>

                    {/* Load more */}
                    {nextPageToken && (
                      <div className="text-center mt-12">
                        <button
                          onClick={loadMore}
                          disabled={loadingMore}
                          className="px-8 py-3 rounded-full text-white font-semibold transition-opacity duration-200 hover:opacity-80 disabled:opacity-40"
                          style={{ backgroundColor: '#2563eb' }}
                        >
                          {loadingMore ? 'Loading…' : 'Load More Videos'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default YouTube;
